import { useEffect, useState } from 'react';

import { Divider, type TypeSelectOption, dialogService, toastService } from '@thefarmersfront/kitchen';

import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { PageTitle } from '~/components/common';
import { DeliveryAgencyOutboundUnitListTable, DeliveryAgencyOutboundUnitSearchBox } from '~/components/delivery-agency';
import { PageWrapper } from '~/components/layout';

import { useToggle } from '~/hooks/common';
import {
  deliveryAgencyKeys,
  useGetOutboundsListQuery,
  useGetOutboundsSearchConditionsQuery,
  usePostOutboundsGroupMutation,
  usePostOutboundsUngroupMutation,
} from '~/hooks/delivery-agency/queries';
import { addIf, renderFilterErrorMessage } from '~/utils/common';
import { formatDate } from '~/utils/date';

import { BREAD_CRUMB, OPTION_ALL, PATH, TOAST_MESSAGE } from '~/constants';
import {
  DELIVERY_AGENCY_SORT_BY,
  INITIAL_DELIVERY_AGENCY_PAGE_CURSOR,
  INITIAL_DELIVERY_AGENCY_PAGE_SIZE,
  type TypeDeliveryAgencySortBy,
} from '~/constants/delivery-agency';
import { DELIVERY_AGENCY } from '~/constants/errorCode';

import type { TypeErrorResponseData } from '~/types/common.types';
import type {
  TypeDeliveryAgencyOutboundUnitListItem,
  TypeDeliveryAgencyOutboundUnitSearchBoxOptions,
  TypeDeliveryAgencyOutboundUnitSearchBoxValues,
  TypeDeliveryAgencyOutboundUnitSearchBoxValuesStatus,
  TypeGetOutboundsListParams,
} from '~/types/delivery-agency.types';

const INITIAL_SEARCH_FILTER_VALUES: TypeDeliveryAgencyOutboundUnitSearchBoxValues = {
  searchKeywordType: null,
  searchKeyword: '',
  estimatedDeliveryDate: dayjs().add(1, 'day').toDate(),
  deliveryType: OPTION_ALL,
  courierCode: OPTION_ALL,
};

const INITIAL_SEARCH_FILTER_VALUES_STATUS: TypeDeliveryAgencyOutboundUnitSearchBoxValuesStatus = {
  searchKeyword: 'default',
  estimatedDeliveryDate: 'default',
};

const INITIAL_SEARCH_FILTER_OPTIONS: TypeDeliveryAgencyOutboundUnitSearchBoxOptions = {
  searchKeywordTypes: [],
  deliveryTypes: [],
  courierCodes: [],
};

const DeliveryAgencyOutboundUnitPage = () => {
  const queryClient = useQueryClient();

  const { data: searchFilterOptions = INITIAL_SEARCH_FILTER_OPTIONS, isError: isSearchConditionsError } =
    useGetOutboundsSearchConditionsQuery({ showGlobalErrorToast: false });

  const [listQueryParams, setListQueryParams] = useState<TypeGetOutboundsListParams | null>(null);
  const {
    data: outboundsListData,
    isError: isOutboundsListError,
    error: outboundsListError,
  } = useGetOutboundsListQuery({
    params: listQueryParams ?? ({} as TypeGetOutboundsListParams),
    enabled: !!listQueryParams,
    showGlobalErrorToast: false,
  });

  const outboundsGroupMutation = usePostOutboundsGroupMutation();
  const outboundsUngroupMutation = usePostOutboundsUngroupMutation();

  const { isToggle: isFilterOpen, handleToggle: handleFilterToggle } = useToggle({ initialState: true });

  const [searchFilterValues, setSearchFilterValues] =
    useState<TypeDeliveryAgencyOutboundUnitSearchBoxValues>(INITIAL_SEARCH_FILTER_VALUES);

  const [selectedItems, setSelectedItems] = useState<TypeDeliveryAgencyOutboundUnitListItem[]>([]);

  const [sortBy, setSortBy] = useState<TypeDeliveryAgencySortBy>(DELIVERY_AGENCY_SORT_BY.CREATED_AT_DESC);

  const [searchFilterValuesStatus, setSearchFilterValuesStatus] =
    useState<TypeDeliveryAgencyOutboundUnitSearchBoxValuesStatus>(INITIAL_SEARCH_FILTER_VALUES_STATUS);

  const listData = outboundsListData?.data ?? null;
  const pagination = {
    currentPage: outboundsListData?.pagination.cursor ?? INITIAL_DELIVERY_AGENCY_PAGE_CURSOR,
    totalCount: outboundsListData?.pagination.listTotal ?? 0,
    pageSize: outboundsListData?.pagination.listView ?? INITIAL_DELIVERY_AGENCY_PAGE_SIZE,
  };

  useEffect(() => {
    if (isSearchConditionsError) {
      toastService.addToast({ type: 'error', persist: true, message: renderFilterErrorMessage() });
    }
  }, [isSearchConditionsError]);

  useEffect(() => {
    if (isOutboundsListError) {
      const error = (outboundsListError as TypeErrorResponseData['error']) ?? null;

      if (error?.code === DELIVERY_AGENCY.BACKEND_ERROR_DIALOG_CODE) {
        dialogService.addDialog({ children: error?.message });
        return;
      }

      toastService.addToast({
        type: 'error',
        message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR,
      });
    }
  }, [isOutboundsListError]);

  useEffect(() => {
    if (searchFilterOptions.searchKeywordTypes.length > 0) {
      setSearchFilterValues((prev) => ({
        ...prev,
        searchKeywordType: prev.searchKeywordType ?? searchFilterOptions.searchKeywordTypes[0] ?? null,
      }));
    }
  }, [searchFilterOptions]);

  const handleResetFilterValues = () => {
    setSearchFilterValues({
      ...INITIAL_SEARCH_FILTER_VALUES,
      searchKeywordType: searchFilterOptions.searchKeywordTypes[0] ?? null,
    });
    setSearchFilterValuesStatus(INITIAL_SEARCH_FILTER_VALUES_STATUS);
  };

  const buildListParams = (
    currentPagination: {
      currentPage: number;
      pageSize: number;
    },
    sortByOption: TypeDeliveryAgencySortBy = sortBy
  ): TypeGetOutboundsListParams => ({
    ...addIf(searchFilterValues.searchKeywordType?.value, 'searchKeywordType'),
    ...addIf(searchFilterValues.searchKeyword.trim(), 'searchKeyword'),
    ...addIf(searchFilterValues.deliveryType?.value, 'deliveryType'),
    ...addIf(formatDate(searchFilterValues.estimatedDeliveryDate, 'YYYY-MM-DD'), 'estimatedDeliveryDate'),
    ...addIf(searchFilterValues.courierCode?.value, 'courierCode'),
    cursor: currentPagination.currentPage,
    limit: currentPagination.pageSize,
    sortBy: sortByOption,
  });

  const handleSearch = (
    currentPagination = { currentPage: INITIAL_DELIVERY_AGENCY_PAGE_CURSOR, pageSize: pagination.pageSize },
    sortByOption: TypeDeliveryAgencySortBy = sortBy
  ) => {
    const params = buildListParams(currentPagination, sortByOption);

    queryClient.invalidateQueries({
      queryKey: deliveryAgencyKeys.outboundsList(params),
    });

    setListQueryParams(params);
    setSelectedItems([]);
  };

  const handleChangePageLimit = (value: TypeSelectOption<number>) => {
    handleSearch({ currentPage: INITIAL_DELIVERY_AGENCY_PAGE_CURSOR, pageSize: value.value });
  };

  const handleChangePage = (page: number) => {
    handleSearch({ currentPage: page, pageSize: pagination.pageSize });
  };

  const handleChangeSort = (value: TypeDeliveryAgencySortBy) => {
    setSortBy(value);
    handleSearch({ currentPage: INITIAL_DELIVERY_AGENCY_PAGE_CURSOR, pageSize: pagination.pageSize }, value);
  };

  const handleChangeRowSelection = (selectedRows: TypeDeliveryAgencyOutboundUnitListItem[]) => {
    setSelectedItems(selectedRows);
  };

  const handleClickGroupOutbounds = () => {
    const orderCounts = selectedItems.reduce((acc, item) => acc + item.orderCount, 0);

    dialogService.addDialog({
      type: 'confirm',
      children: `선택한 출고번호에 속한 주문${orderCounts}개를 1개로 합포장 하시겠습니까?\n합포장 처리 시 출고번호와 운송장 번호가\n변경됩니다.`,
      onConfirm: () => {
        const outboundCodes = selectedItems.map((item) => item.outboundCode);

        outboundsGroupMutation.mutate(
          { outboundCodes },
          {
            onSuccess: () => {
              toastService.addToast({ message: '합포장 되었습니다.' });
              handleSearch();
            },
            onError: (error) => {
              if (error?.code === DELIVERY_AGENCY.BACKEND_ERROR_DIALOG_CODE) {
                dialogService.addDialog({ children: error?.message });
                return;
              }
              toastService.addToast({ type: 'error', message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR });
            },
          }
        );
      },
    });
  };

  const handleClickUngroupOutbounds = () => {
    const outboundCodes = selectedItems.map((item) => item.outboundCode);

    dialogService.addDialog({
      type: 'confirm',
      children: `${outboundCodes.join(
        ', '
      )}\n출고번호의 합포를 취소하시겠습니까?\n합포장 취소 시 출고번호에 속한 주문번호\n기준으로 합포 단위가 나눠집니다.`,
      onConfirm: () => {
        outboundsUngroupMutation.mutate(
          { outboundCodes },
          {
            onSuccess: () => {
              toastService.addToast({ message: '합포장이 취소되었습니다.' });
              handleSearch();
            },
            onError: (error) => {
              if (error?.code === DELIVERY_AGENCY.BACKEND_ERROR_DIALOG_CODE) {
                dialogService.addDialog({ children: error?.message });
                return;
              }
              toastService.addToast({ type: 'error', message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR });
            },
          }
        );
      },
    });
  };

  return (
    <PageWrapper>
      <PageTitle
        title="합포 단위 관리"
        breadcrumbs={BREAD_CRUMB[PATH.DELIVERY_AGENCY_OUTBOUND_UNIT]}
        handleFilterToggle={handleFilterToggle}
      />
      {isFilterOpen && (
        <>
          <DeliveryAgencyOutboundUnitSearchBox
            options={searchFilterOptions}
            values={searchFilterValues}
            valuesStatus={searchFilterValuesStatus}
            onChange={(values) => setSearchFilterValues((prev) => ({ ...prev, ...values }))}
            onChangeStatus={(status) => setSearchFilterValuesStatus((prev) => ({ ...prev, ...status }))}
            onSearch={handleSearch}
            onReset={handleResetFilterValues}
          />
          <Divider color="gray100" />
        </>
      )}
      <DeliveryAgencyOutboundUnitListTable
        data={listData}
        pagination={pagination}
        sortBy={sortBy}
        selectedItems={selectedItems}
        onChangePageLimit={handleChangePageLimit}
        onChangeSort={handleChangeSort}
        onChangePage={handleChangePage}
        onChangeRowSelection={handleChangeRowSelection}
        onClickGroupOutbounds={handleClickGroupOutbounds}
        onClickUngroupOutbounds={handleClickUngroupOutbounds}
      />
    </PageWrapper>
  );
};

export default DeliveryAgencyOutboundUnitPage;
